"use client"

import { useEffect } from "react"

interface ClientTrackingProps {
  perfilId: string
  slug: string
}

export function ClientTracking({ perfilId, slug }: ClientTrackingProps) {
  // Helper function to conditionally log based on environment
  const devLog = (type: "log" | "error" | "warn", message: string) => {
    // Only log in development environment
    if (process.env.NODE_ENV === "development") {
      if (type === "log") console.log(message)
      else if (type === "error") console.error(message)
      else if (type === "warn") console.warn(message)
    }
  }

  useEffect(() => {
    // Only run in browser environment
    if (typeof window === "undefined") return

    // Track the page view on the client side
    const trackView = async () => {
      try {
        // Prepare the payload
        const payload = {
          perfilId,
          component: "profile_view",
          yyyymm: new Date().toISOString().slice(0, 7).replace("-", ""),
          slug,
        }

        // Use the current origin instead of relying on environment variables
        // This ensures we're making requests to the same server that's serving the page
        const origin = window.location.origin
        const trackingUrl = `${origin}/api/track`

        // Log the URL we're about to fetch (only in development)
        devLog("log", `[TRACK] [DEBUG] Fetching: ${trackingUrl}`)

        // Make the fetch request with a timeout
        const controller = new AbortController()
        const timeoutId = setTimeout(() => controller.abort(), 5000) // 5 second timeout

        const response = await fetch(trackingUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
          cache: "no-store",
          signal: controller.signal,
        })

        // Clear the timeout
        clearTimeout(timeoutId)

        if (response.ok) {
          devLog("log", `[TRACK] [SUCCESS] ${slug} profile_view - Client-side tracking`)
        } else {
          devLog("error", `[TRACK] [FAILED] ${slug} profile_view - API call failed with status ${response.status}`)
        }
      } catch (error) {
        // Provide more detailed error information
        const errorMessage = error instanceof Error ? error.message : String(error)
        devLog("error", `[TRACK] [FAILED] ${slug} profile_view - ${errorMessage}`)

        // Try a fallback approach for mobile browsers
        try {
          // Create a fallback tracking approach using a simple image pixel
          // This is more likely to work on mobile browsers with restrictive settings
          const img = new Image()
          const timestamp = new Date().getTime()

          // Use the current origin for the pixel URL
          const origin = window.location.origin
          img.src = `${origin}/api/pixel?id=${perfilId}&slug=${encodeURIComponent(slug)}&t=${timestamp}`

          // Log the fallback attempt
          devLog("log", `[TRACK] [FALLBACK] ${slug} profile_view - Using pixel tracking`)
        } catch (fallbackError) {
          // If even the fallback fails, just log it
          devLog("error", `[TRACK] [FAILED] ${slug} profile_view - Fallback tracking failed`)
        }
      }
    }

    // Execute the tracking function with a slight delay to ensure the page is fully loaded
    setTimeout(trackView, 500)
  }, [perfilId, slug])

  // This component doesn't render anything
  return null
}
